/**
 * internal-app-platform module - English translations
 */
export const en = {
  pageTitle: 'My Apps',
  pageSubtitle: 'Deploy via Claude Code conversation — this page is only for onboarding and viewing',

  onboard: {
    sectionTitle: '1. Onboard to Claude Code',
    statusNone: 'No token generated yet',
    statusActive: '✓ Active ({days} days left / expires {date})',
    statusExpiring: '⚠ Expiring soon ({days} days left)',
    statusExpired: '✗ Expired ({date})',
    statusRevoked: 'Revoked on {date}',
    instructionHint:
      'Run the command in your shell terminal (NOT inside Claude Code). After it succeeds, start Claude Code and verify with `claude mcp list`.',
    copyMaskedHint:
      'Token plaintext is not shown on the page. Copying the full command will include the plaintext (only visible at generation time).',
    deployPromptTitle: '💡 Deployment prompt (recommended)',
    deployPromptTemplate:
      'Use the deploy_prepare tool from the ffoa-apps MCP to deploy this project to the internal app platform with app name <change-this-to-your-app-name>. Do NOT generate Dockerfile, systemd, nginx, or any deployment config files — the platform handles them automatically.',
    deployPromptHint:
      'Just saying "deploy this" often makes Claude take the generic path (generating Dockerfile / nginx / systemd files you do not need). This template forces it to use the MCP.',
    toastDeployPromptCopied: 'Deployment prompt copied to clipboard',
    actions: {
      generate: 'Generate new token',
      regenerate: 'Regenerate',
      revoke: 'Revoke',
      copyCommand: 'Copy full command',
      copyDeployPrompt: 'Copy deployment prompt',
      copied: 'Copied',
    },
    regenerateConfirmTitle: 'Regenerate token?',
    regenerateConfirmBody:
      'The old token will be invalidated immediately. Existing Claude Code sessions must re-run `claude mcp add --transport http ...` in shell.\nIf you just want to add a new device, you do NOT need to regenerate — one token works on multiple devices.',
    regenerateConfirmCancel: 'Cancel',
    regenerateConfirmSubmit: 'Regenerate',
    maskedCopied: '•••••••• (copied to clipboard)',
    maskedCopyHint: '•••••••• (click "Copy full command" to copy)',
    revokeConfirmTitle: 'Revoke token?',
    revokeConfirmBody:
      'All devices using this token will lose access immediately. A new token is required to re-onboard.',
    revokeConfirmInputLabel: 'Type "REVOKE" to confirm',
    revokeConfirmPlaceholder: 'REVOKE',
    revokeConfirmCancel: 'Cancel',
    revokeConfirmSubmit: 'Revoke',
    toastIssued: 'New token generated. The full command has been copied to your clipboard — paste it now.',
    toastRevoked: 'Token revoked',
    toastCopied: 'Full command copied to clipboard',
    toastUrlCopied: 'URL copied to clipboard',
    toastCopyFailed: 'Failed to copy. Please copy manually from the command above.',
    toastError: 'Operation failed, please retry',
    expiringWarning: 'Token is expiring soon — consider regenerating.',
    expiredHint: 'Token has expired. Generate a new token to re-onboard.',
    revokedHint: 'Token was revoked. Generate a new token to re-onboard.',
  },

  apps: {
    sectionTitle: '2. My deployed apps',
    sectionTitleWithCount: '2. My deployed apps ({count})',
    empty: 'No apps deployed yet',
    emptyHint: 'After onboarding above, say "deploy xxx tool" to Claude Code',
    showDestroyed: 'Show destroyed',
    hideDestroyed: 'Hide destroyed',
    columns: {
      slug: 'Name',
      url: 'URL',
      status: 'Status',
      lastDeployed: 'Last deploy',
      actions: 'Actions',
    },
    status: {
      PENDING: 'Pending',
      BUILDING: 'Building',
      HEALTHY: 'Running',
      FAILED: 'Build failed',
      DISABLED: 'Suspended by IT',
      DESTROYED: 'Destroyed',
      DISABLED_ARCHIVED: 'Disabled',
      PURGED: 'Purged',
    },
    destroyedWithRecover: 'Destroyed · recoverable until {date}',
    actions: {
      copyUrl: 'Copy URL',
      open: 'Open',
    },
    urlOffline: '— (URL offline)',
    destroyedAt: 'Destroyed at {date}',
    lastDeployedAt: 'Last deployed {date}',
  },

  usageGuide: {
    sectionTitle: 'Usage Guide / FAQ',
    sectionHint: 'First time? Not sure what to say to Claude next? Expand for the full walkthrough.',
    fullDocLink: 'Full guide with screenshots: docs/modules/internal-app-platform/13-usage-guide.md',
    steps: [
      {
        title: 'Step 1 · Copy the mcp add command',
        body: 'Above, click "Generate new token" or "Regenerate". The plaintext token is shown only at the moment of generation; "Copy full command" puts the entire claude mcp add ... line (with plaintext token) into your clipboard.',
      },
      {
        title: 'Step 2 · Run in a regular terminal (NOT inside Claude Code)',
        body: 'Open bash / zsh / PowerShell, paste, hit enter. Then restart Claude Code and run `claude mcp list` — you should see ffoa-apps with ✓ Connected.',
      },
      {
        title: 'Step 3 · Use the deployment-prompt template above to ask Claude to deploy',
        body: 'Copy the yellow callout text, swap in your app name, send it to Claude. Claude will call deploy_prepare → git push → platform webhook → build → online (30 sec – 5 min).',
      },
      {
        title: 'Step 4 · Wait ~5 minutes, then open the URL',
        body: 'Push complete ≠ online. Wait ~5 minutes, refresh "My deployed apps" below; once status=Running you can click "Open". Still 502? Tell Claude "show me the logs".',
      },
    ],
    faqTitle: 'FAQ',
    faq: [
      {
        q: 'Claude generated a Dockerfile / nginx.conf / GitHub Actions for me',
        a: 'You did not use the deployment-prompt template — Claude took the generic path. Tell Claude to delete those files, then re-send the prompt copied from this page.',
      },
      {
        q: 'claude mcp list does not show ffoa-apps',
        a: '90% of the time you pasted the mcp add command inside Claude Code instead of a regular shell. Run it in bash/zsh/PowerShell. Watch out for broken \\ line continuations.',
      },
      {
        q: 'URL does not open / 502 / self-signed cert warning right after deploy',
        a: 'Normal for the first 5 minutes (webhook still building). If still broken after 5 min, tell Claude "show the logs" — usually a missing env var.',
      },
      {
        q: 'Error: unsupported_runtime',
        a: 'MVP only supports Node (package.json + start script) and pure static sites (root index.html). Python/Go/Java/Docker are not yet supported.',
      },
      {
        q: 'Error: app_in_terminal_state',
        a: 'This slug was destroyed before; the 30-day retention window forbids reuse. Pick a different slug.',
      },
      {
        q: 'Lost my token',
        a: 'No recovery. Click "Regenerate" — the old token dies instantly and every device must re-run claude mcp add.',
      },
    ],
    boundariesTitle: 'Boundaries (what you cannot do)',
    boundaries: [
      'Cannot modify other employees’ apps (physical isolation by employee slug)',
      'Cannot use reserved slugs (admin / api / www / mail / login, etc.)',
      'Destroyed apps are recoverable by IT within 30 days, then physically purged',
      'Each app gets fixed 256 MB RAM / 0.5 CPU; exceeding causes OOM kill (logs show Killed)',
      'No persistent disk (stateless containers); use the company DB / object storage for data',
    ],
  },

  errors: {
    loadStatus: 'Failed to load token status',
    loadApps: 'Failed to load app list',
    retry: 'Retry',
  },
};
